import * as React from "react";
import { PanelRightOpen } from "lucide-react";

import { cn } from "@/lib/utils";
import { useIsMobile } from "../../hooks/use-mobile";

type SidebarSide = "left" | "right";

interface SidebarContextValue {
  open: boolean;
  setOpen: (open: boolean) => void;
  toggle: () => void;
  side: SidebarSide;
  width: number;
  setWidth: (width: number) => void;
  isMobile: boolean;
}

const SidebarContext = React.createContext<SidebarContextValue | null>(null);

export const useSidebar = () => {
  const context = React.useContext(SidebarContext);
  if (!context) {
    throw new Error("useSidebar must be used within a SidebarProvider");
  }
  return context;
};

export interface SidebarProviderProps {
  children: React.ReactNode;
  defaultOpen?: boolean;
  side?: SidebarSide;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  onOpenChange?: (open: boolean) => void;
}

export const SidebarProvider = ({
  children,
  defaultOpen = false,
  width = 420,
  minWidth = 420,
  maxWidth = 640,
  side = "left",
  onOpenChange
}: SidebarProviderProps) => {
  const isMobile = useIsMobile();
  const [open, setOpen] = React.useState(defaultOpen);
  const [panelWidth, setPanelWidth] = React.useState(width);

  const clampWidth = React.useCallback(
    (value: number) => Math.min(maxWidth, Math.max(minWidth, value)),
    [minWidth, maxWidth]
  );

  const setWidth = React.useCallback(
    (value: number) => {
      setPanelWidth(clampWidth(value));
    },
    [clampWidth]
  );

  React.useEffect(() => {
    setPanelWidth(clampWidth(width));
  }, [width, clampWidth]);

  const toggle = React.useCallback(() => setOpen((previous) => !previous), []);

  React.useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  const value = React.useMemo(
    () => ({
      open,
      setOpen,
      toggle,
      side,
      width: panelWidth,
      setWidth,
      isMobile
    }),
    [open, side, panelWidth, setWidth, isMobile, toggle]
  );

  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
};

const getSidebarTransform = (side: SidebarSide, open: boolean) => {
  if (open) return "translate-x-0";
  return side === "right" ? "translate-x-full" : "-translate-x-full";
};

export interface SidebarProps extends React.HTMLAttributes<HTMLDivElement> {
  side?: SidebarSide;
}

export const Sidebar = React.forwardRef<HTMLDivElement, SidebarProps>(
  ({ className, children, side: propSide, style, ...props }, ref) => {
    const { open, setOpen, side: contextSide, isMobile, width } = useSidebar();
    const side = propSide ?? contextSide;
    const widthValue = `${width}px`;

    const computedStyle: React.CSSProperties = {
      ...style,
      ["--sidebar-width" as string]: widthValue,
      width: isMobile ? undefined : widthValue,
      maxWidth: widthValue
    };

    return (
      <>
        {/* Mobile overlay */}
        <button
          className={cn(
            "fixed inset-0 z-40 bg-black/50 transition-opacity duration-300 focus-visible:outline-none lg:hidden",
            open ? "opacity-100" : "pointer-events-none opacity-0"
          )}
          type="button"
          aria-label="Close sidebar"
          onClick={() => setOpen(false)}
        />
        <aside
          ref={ref}
          data-state={open ? "open" : "closed"}
          className={cn(
            "fixed bottom-0 top-0 z-50 flex h-full max-w-full flex-col overflow-hidden border-l border-sidebar-border/70 bg-sidebar/90 text-sidebar-foreground shadow-2xl backdrop-blur-xl transition-[transform,opacity] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] supports-[backdrop-filter]:bg-sidebar",
            side === "right" ? "right-0" : "left-0",
            getSidebarTransform(side, open),
            !open && !isMobile ? "pointer-events-none opacity-0" : "opacity-100",
            className
          )}
          style={computedStyle}
          aria-hidden={!open && !isMobile}
          {...props}
        >
          <div
            className={cn(
              "flex h-full w-[min(100%,var(--sidebar-width))] flex-col overflow-hidden transition-opacity duration-300 ease-[cubic-bezier(0.22,1,0.36,1)]",
              !isMobile && !open ? "pointer-events-none opacity-0" : "opacity-100"
            )}
          >
            {children}
          </div>
        </aside>
      </>
    );
  }
);
Sidebar.displayName = "Sidebar";

export type SidebarInsetProps = React.HTMLAttributes<HTMLDivElement>;

export const SidebarInset = React.forwardRef<HTMLDivElement, SidebarInsetProps>(
  ({ className, style, ...props }, ref) => (
    <div ref={ref} className={cn("flex-1", className)} style={style} {...props} />
  )
);
SidebarInset.displayName = "SidebarInset";

export type SidebarMainProps = React.HTMLAttributes<HTMLDivElement> & {
  /** Extra right-side space to reserve (px), e.g. for a second right-hand panel like the
   *  agent-chat drawer. Added on top of this sidebar's own offset so content squishes for both. */
  reservedRight?: number;
};

export const SidebarMain = React.forwardRef<HTMLDivElement, SidebarMainProps>(
  ({ className, style, reservedRight = 0, ...props }, ref) => {
    const { open, isMobile, side, width } = useSidebar();
    const dynamicStyle: React.CSSProperties = { ...style };

    if (!isMobile) {
      const offset = open ? width : 0;
      if (side === "right") {
        dynamicStyle.marginRight = `${offset + reservedRight}px`;
        dynamicStyle.marginLeft = style?.marginLeft ?? "0px";
      } else {
        dynamicStyle.marginLeft = `${offset}px`;
        dynamicStyle.marginRight = `${reservedRight}px`;
      }
    } else {
      dynamicStyle.marginRight = style?.marginRight;
      dynamicStyle.marginLeft = style?.marginLeft;
    }

    return (
      <div
        ref={ref}
        className={cn(
          "flex-1 overflow-auto transition-[margin] duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] [will-change:margin]",
          className
        )}
        style={dynamicStyle}
        {...props}
      />
    );
  }
);
SidebarMain.displayName = "SidebarMain";

export type SidebarSectionProps = React.HTMLAttributes<HTMLDivElement>;

export const SidebarHeader = React.forwardRef<HTMLDivElement, SidebarSectionProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("px-6 pb-4 pt-6", className)} {...props} />
));
SidebarHeader.displayName = "SidebarHeader";

export const SidebarContent = React.forwardRef<HTMLDivElement, SidebarSectionProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("flex-1 overflow-y-auto px-6 pb-6", className)} {...props} />
));
SidebarContent.displayName = "SidebarContent";

export const SidebarFooter = React.forwardRef<HTMLDivElement, SidebarSectionProps>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn("border-t border-sidebar-border/40 px-6 py-4", className)} {...props} />
));
SidebarFooter.displayName = "SidebarFooter";

export type SidebarTriggerProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export const SidebarTrigger = React.forwardRef<HTMLButtonElement, SidebarTriggerProps>(
  ({ className, children, ...props }, ref) => {
    const { toggle } = useSidebar();
    return (
      <button
        ref={ref}
        onClick={toggle}
        className={cn(
          "inline-flex items-center gap-2 rounded-md border border-border bg-background/70 px-3 py-2 text-sm font-medium text-foreground shadow-sm transition-colors hover:bg-background",
          className
        )}
        type="button"
        {...props}
      >
        {children ?? (
          <>
            <PanelRightOpen className="h-4 w-4" />
            <span>Toggle panel</span>
          </>
        )}
      </button>
    );
  }
);
SidebarTrigger.displayName = "SidebarTrigger";

export interface SidebarCloseProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  children?: React.ReactNode;
}

export const SidebarClose = React.forwardRef<HTMLButtonElement, SidebarCloseProps>(
  ({ className, children, ...props }, ref) => {
    const { setOpen } = useSidebar();
    return (
      <button
        ref={ref}
        type="button"
        onClick={() => setOpen(false)}
        className={cn(
          "inline-flex items-center rounded-md bg-transparent px-2 py-1 text-sm text-sidebar-foreground/70 transition hover:text-sidebar-foreground",
          className
        )}
        {...props}
      >
        {children ?? "Close"}
      </button>
    );
  }
);
SidebarClose.displayName = "SidebarClose";
